import { useMemo } from "react";
import { toIdParam, usePayrollListParams } from "../payroll-lists.params";
import { Download, Lock, Search } from "lucide-react";
import ReactCountryFlag from "react-country-flag";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Table, type ColumnDef } from "@/components/table";
import { PageDataStats } from "@/components/PageDataStats";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/utils/cn";
import { usePageTitle } from "@/hooks/usePageTitle";
import { withBranding, formatDateDmy } from "@/utils/helper";
import { PayrollRunStatusEnum } from "@/utils/enums/payroll.enum";
import {
  useListPayrollBranchesQuery,
  useListPayslipsQuery,
  useGetPayslipStatsQuery,
  type IPayslipListItem,
} from "@/services/(company)/payrollRunsApi";
import { useGetAllUserDepartmentsQuery } from "@/services/(company)/departmentsApi";
import { useDownloadPayslip } from "@/services/(company)/payslipDownload";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";

const buildStatusOptions = (translate: TFunction) => [
  { value: "all", label: translate("company:payrolls.all_statuses") },
  { value: "PAID", label: translate("company:payrolls.paid") },
  { value: "PENDING", label: translate("common:status.pending") },
  { value: "HOLD", label: translate("company:payrolls.hold") },
  { value: "EXCLUDED", label: translate("company:payrolls.excluded") },
];

const buildStatusStyles = (
  translate: TFunction,
): Record<string, { dot: string; chip: string; label: string }> => ({
  PAID: {
    dot: "bg-success",
    chip: "bg-success/10 text-success",
    label: translate("company:payrolls.paid"),
  },
  PENDING: {
    dot: "bg-yellow-500",
    chip: "bg-warning-muted text-yellow-700",
    label: translate("common:status.pending"),
  },
  HOLD: {
    dot: "bg-red-500",
    chip: "bg-red-50 text-red-600",
    label: translate("company:payrolls.hold"),
  },
  EXCLUDED: {
    dot: "bg-gray-400",
    chip: "bg-gray-100 text-gray-600",
    label: translate("company:payrolls.excluded"),
  },
});

const FilterSelect = ({
  value,
  onChange,
  options,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  placeholder: string;
}) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="h-9 w-36 bg-white text-xs font-medium">
      <SelectValue placeholder={placeholder} />
    </SelectTrigger>
    <SelectContent>
      {options.map((o) => (
        <SelectItem key={o.value} value={o.value}>
          {o.label}
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
);

const getInitials = (name: string) =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("") || "?";

const formatAmount = (v: number | string) => {
  const n = typeof v === "string" ? Number(v.replace(/,/g, "")) : v;
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const safeDate = (iso: string) => {
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "—" : formatDateDmy(d);
};

export const CompanyPaySlips = () => {
  const { t: translate } = useTranslation(["company", "common"]);
  usePageTitle(withBranding(translate("company:payrolls.pay_slips")));

  // Search, filters and page all live in the URL — see useTableParams.
  const tp = usePayrollListParams();
  const { branch, department, status } = tp.filters;

  const { data: branchesResponse } = useListPayrollBranchesQuery();
  const branches = branchesResponse?.data ?? [];

  const { data: departmentsResponse } = useGetAllUserDepartmentsQuery({});
  const departments = departmentsResponse?.data ?? [];

  const branchOptions = [
    { value: "all", label: translate("common:ui_kit.all_branches") },
    ...branches.map((b) => ({ value: String(b.id), label: b.name })),
  ];

  const departmentOptions = [
    { value: "all", label: translate("company:payrolls.all_departments") },
    ...departments.map((d: { id: number | string; name: string }) => ({
      value: String(d.id),
      label: d.name,
    })),
  ];

  const { currentData, isFetching } = useListPayslipsQuery({
    page: tp.page,
    limit: 20,
    ...(tp.committedSearch ? { search: tp.committedSearch } : {}),
    ...(toIdParam(branch) !== undefined
      ? { branchId: toIdParam(branch)! }
      : {}),
    ...(toIdParam(department) !== undefined
      ? { departmentId: toIdParam(department)! }
      : {}),
    ...(status !== "all" ? { status } : {}),
  });

  const rows = useMemo<IPayslipListItem[]>(
    () => currentData?.data ?? [],
    [currentData],
  );
  const totalPages =
    currentData?.meta?.totalPages ?? currentData?.meta?.lastPage ?? 1;

  const { data: statsRes, isLoading: statsLoading } = useGetPayslipStatsQuery({
    ...(toIdParam(branch) !== undefined
      ? { branchId: toIdParam(branch)! }
      : {}),
    ...(toIdParam(department) !== undefined
      ? { departmentId: toIdParam(department)! }
      : {}),
  });
  const stats = statsRes?.data;
  // Each tile is the status filter it counts; the total is "no filter".
  const statCards = [
    {
      name: translate("company:payrolls.total_payslips"),
      value: stats?.total ?? 0,
      icon: "/icons/interface-icons/stats/payroll/icon-3d-cash-envelop.png",
      ...tp.statFilter({ status: "all" }),
    },
    {
      name: translate("company:payrolls.paid"),
      value: stats?.paid ?? 0,
      icon: "/icons/interface-icons/stats/shared/icon-3d-green-check.png",
      ...tp.statFilter({ status: "PAID" }),
    },
    {
      name: translate("common:status.pending"),
      value: stats?.pending ?? 0,
      icon: "/icons/interface-icons/stats/shared/icon-3d-clock-gold.png",
      ...tp.statFilter({ status: "PENDING" }),
    },
    {
      name: translate("company:payrolls.hold"),
      value: stats?.hold ?? 0,
      icon: "/icons/interface-icons/stats/shared/icon-3d-pause.png",
      ...tp.statFilter({ status: "HOLD" }),
    },
    {
      name: translate("company:payrolls.excluded"),
      value: stats?.excluded ?? 0,
      icon: "/icons/interface-icons/stats/shared/icon-3d-red-x.png",
      ...tp.statFilter({ status: "EXCLUDED" }),
    },
  ];

  const { download, isDownloading } = useDownloadPayslip();

  const columns: ColumnDef<IPayslipListItem>[] = [
    {
      key: "employee",
      label: translate("common:labels.employee"),
      className: "w-[24%]",
      render: (r) => (
        <div className="flex items-center gap-3">
          <Avatar className="size-9">
            {r.employee.profileImage && (
              <AvatarImage
                src={r.employee.profileImage}
                alt={r.employee.fullName}
              />
            )}
            <AvatarFallback className="bg-primary/10 text-primary text-xs font-bold">
              {getInitials(r.employee.fullName)}
            </AvatarFallback>
          </Avatar>
          <div className="flex flex-col min-w-0">
            <span className="text-sm font-semibold text-gray-900 truncate">
              {r.employee.fullName}
            </span>
            <span className="text-[11px] text-gray-400 truncate">
              {r.employee.employeeCode}
            </span>
          </div>
        </div>
      ),
    },
    {
      key: "branchDept",
      label: translate("company:payrolls.branch_dept"),
      className: "w-[18%]",
      render: (r) => (
        <div className="flex flex-col min-w-0">
          <span className="text-sm text-gray-900 truncate">
            {r.branch?.name ?? "—"}
          </span>
          <span className="text-[11px] text-gray-400 truncate">
            {r.department?.name ?? "—"}
          </span>
        </div>
      ),
    },
    {
      key: "country",
      label: translate("company:payrolls.country"),
      className: "w-[14%]",
      render: (r) => {
        if (!r.nationality) return <span className="text-gray-300">—</span>;
        return (
          <div className="flex items-center gap-2">
            <ReactCountryFlag
              countryCode={r.nationality.code}
              svg
              style={{ width: "1.4em", height: "1em", borderRadius: "1px" }}
            />
            <span className="text-sm text-gray-700 truncate">
              {r.nationality.name}
            </span>
          </div>
        );
      },
    },
    {
      key: "net",
      label: translate("company:payrolls.net_short"),
      className: "w-[12%]",
      render: (r) => (
        <span className="text-sm font-medium text-gray-900">
          {r.currency?.code ?? "—"} {formatAmount(r.netPay)}
        </span>
      ),
    },
    {
      key: "period",
      label: translate("company:payrolls.pay_period"),
      className: "w-[12%]",
      render: (r) => (
        <div className="flex flex-col">
          <span className="text-sm text-gray-700">{r.payPeriod}</span>
          <span className="text-[11px] text-gray-400">
            {safeDate(r.payDate)}
          </span>
        </div>
      ),
    },
    {
      key: "status",
      label: translate("company:payrolls.payment_status"),
      className: "w-[10%]",
      render: (r) => {
        const styles =
          buildStatusStyles(translate)[r.status] ??
          buildStatusStyles(translate).PENDING;
        return (
          <span
            className={cn(
              "inline-flex items-center gap-1 rounded-xs px-2.5 py-1 text-xs font-semibold",
              styles.chip,
            )}
          >
            <span className={cn("size-1.5 rounded-full", styles.dot)} />
            {styles.label}
          </span>
        );
      },
    },
    {
      key: "actions",
      label: translate("company:payrolls.actions"),
      className: "w-[10%]",
      render: (r) => {
        const canDownload = [
          PayrollRunStatusEnum.APPROVED,
          PayrollRunStatusEnum.PROCESSING,
          PayrollRunStatusEnum.COMPLETED,
        ].includes(r.runStatus as PayrollRunStatusEnum);
        // Not downloadable → show the reason instead of a mystery-disabled
        // button (disabled buttons don't surface their tooltip).
        if (!canDownload) {
          return (
            <span
              title={translate(
                "company:payrolls.payslip_can_be_downloaded_once_the_payroll_run_is_approv",
              )}
              className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-border bg-muted/50 px-2.5 py-1 text-[11px] font-medium text-muted-foreground"
            >
              <Lock size={11} className="shrink-0" />
              {translate("company:payrolls.available_after_approval")}
            </span>
          );
        }
        return (
          <Button
            type="button"
            variant="outline-success"
            size="icon-sm"
            disabled={isDownloading}
            data-tooltip-id="hrm-tooltip"
            data-tooltip-content={translate(
              "company:payrolls.download_payslip",
            )}
            onClick={() => download(r.id)}
          >
            <Download size={14} />
          </Button>
        );
      },
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <PageDataStats
        data={statCards}
        loading={statsLoading}
        className="sm:grid-cols-3 lg:grid-cols-5"
      />
      <div className="rounded-xs border border-border bg-white flex flex-col gap-4 p-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-2 border border-input rounded-xs h-9 px-3 w-80 bg-white">
            <Search size={14} className="text-gray-400" />
            <input
              type="text"
              value={tp.search}
              onChange={(e) => tp.setSearch(e.target.value)}
              placeholder={translate(
                "company:payrolls.search_employee_name_status",
              )}
              className="flex-1 bg-transparent text-xs placeholder:text-gray-400 focus:outline-hidden"
            />
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <FilterSelect
              value={branch}
              onChange={(v) => tp.setFilter("branch", v)}
              options={branchOptions}
              placeholder={translate("common:ui_kit.branch")}
            />
            <FilterSelect
              value={department}
              onChange={(v) => tp.setFilter("department", v)}
              options={departmentOptions}
              placeholder={translate("common:labels.department")}
            />
            <FilterSelect
              value={status}
              onChange={(v) => tp.setFilter("status", v)}
              options={buildStatusOptions(translate)}
              placeholder={translate("company:payrolls.status")}
            />
          </div>
        </div>

        <Table<IPayslipListItem>
          noPadding
          columns={columns}
          data={rows}
          loading={isFetching}
          pagination={{
            totalPages,
            currentPage: tp.page,
            onPageChange: tp.setPage,
          }}
        />
      </div>
    </div>
  );
};

export default CompanyPaySlips;
